import React, { useState, useRef, useEffect } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BookImageGallery = ({ images = [], mediaItems = [], title = '' }) => {
  // Get all available images from both sources
  const allImages = [];
  
  // Add images from image_url_product array
  if (images && Array.isArray(images)) {
    images.filter(Boolean).forEach(img => {
      if (!allImages.some(existing => existing.url === img)) {
        allImages.push({ type: 'image', url: img, alt: `Imagen de ${title}` });
      }
    });
  }
  
  // Add media items if available
  if (mediaItems && Array.isArray(mediaItems)) {
    mediaItems.filter(item => item?.url && item.url.trim() !== '').forEach(item => {
      if (!allImages.some(existing => existing.url === item.url)) {
        allImages.push(item);
      }
    });
  }
  
  const mediaContent = allImages;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Helper function to check if URL is a video
  const isVideoUrl = (url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];
    const lowerUrl = url.toLowerCase();
    return videoExtensions.some(ext => lowerUrl.includes(ext)) || 
           lowerUrl.includes('youtube.com') || 
           lowerUrl.includes('vimeo.com') ||
           lowerUrl.includes('youtu.be');
  };

  // Helper function to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : url;
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => 
      prev === 0 ? mediaContent?.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) => 
      prev === mediaContent?.length - 1 ? 0 : prev + 1
    );
  };

  const handleThumbnailClick = () => {
    const currentItem = mediaContent?.[currentIndex];
    if (currentItem?.type === 'image') {
      setIsModalOpen(true);
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleImageClick = (e) => {
    e.stopPropagation();
    if (zoomLevel === 1) {
      setZoomLevel(2);
    } else {
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.5, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.5, 0.5));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleRotateLeft = () => {
    // Removed
  };

  const handleRotateRight = () => {
    // Removed
  };

  // Touch and mouse event handlers for mobile support
  const handleStart = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setDragStart({
        x: clientX - imagePosition.x,
        y: clientY - imagePosition.y
      });
    }
  };

  const handleMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setImagePosition({
        x: clientX - dragStart.x,
        y: clientY - dragStart.y
      });
    }
  };

  const handleMouseDown = (e) => handleStart(e);
  const handleMouseMove = (e) => handleMove(e);
  const handleTouchStart = (e) => {
    e.preventDefault();
    handleStart(e);
  };
  const handleTouchMove = (e) => {
    e.preventDefault();
    handleMove(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isModalOpen) return;
      
      switch (e.key) {
        case 'Escape':
          handleCloseModal();
          break;
        case 'ArrowLeft':
          handlePrev();
          break;
        case 'ArrowRight':
          handleNext();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isModalOpen, mediaContent?.length]);

  if (!mediaContent || mediaContent?.length === 0) {
    return (
      <div className="w-full aspect-[3/4] bg-muted rounded-lg flex items-center justify-center">
        <Icon name="BookOpen" size={64} className="text-muted-foreground" />
      </div>
    );
  }

  const currentItem = mediaContent[currentIndex];

  return (
    <div className="space-y-4">
      {/* Main Media */}
      <div className="relative group">
        <div 
          className={`relative overflow-hidden rounded-lg shadow-subtle ${
            currentItem?.type === 'image' ? 'cursor-zoom-in' : ''
          } aspect-[3/4] flex items-center justify-center`}
          style={{
            backgroundImage: currentIndex > 0 ? 'none' : 'url(/assets/images/background_image.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            backgroundColor: currentIndex > 0 ? '#f8f9fa' : 'transparent'
          }}
          onClick={handleThumbnailClick}
        >
          <div className="flex items-center justify-center w-full h-full">
            {currentItem?.type === 'image' ? (
              <Image
                src={currentItem.url}
                alt={currentItem?.alt || `${title} - Vista ${currentIndex + 1}`}
                className={`w-[180px] h-[255px] sm:w-[240px] sm:h-[340px] object-cover rounded ${currentIndex === 0 ? 'shadow-lg' : ''} transition-transform duration-300 hover:scale-105`}
              />
            ) : currentItem?.type === 'video' ? (
              <video
                src={currentItem.url}
                controls
                className="w-full h-full object-cover"
                poster={images?.[0]}
              >
                Tu navegador no soporta videos.
              </video>
            ) : null}
          </div>

          {/* Navigation Arrows - Only show if multiple items */}
          {mediaContent?.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e?.stopPropagation();
                  handlePrev();
                }}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="ChevronLeft" size={20} />
              </Button>
              
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleNext();
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white hover:bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Icon name="ChevronRight" size={20} />
              </Button>
            </>
          )}

          {/* Zoom Indicator - Only for images */}
          {currentItem?.type === 'image' && (
            <div className="absolute top-2 right-2 bg-black/50 text-white px-2 py-1 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity">
              <Icon name="Maximize2" size={16} className="inline mr-1" />
              Ver completa
            </div>
          )}
          
          {/* Media Type Indicator */}
          <div className="absolute top-2 left-2 bg-black/50 text-white px-2 py-1 rounded text-xs">
            <Icon name={currentItem?.type === 'video' ? 'Play' : 'Image'} size={16} className="inline mr-1" />
            {currentItem?.type === 'video' ? 'Video' : 'Imagen'}
          </div>
        </div>

        {/* Media Counter */}
        {mediaContent?.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/70 text-white px-2 py-1 rounded text-sm">
            {currentIndex + 1} / {mediaContent?.length}
          </div>
        )}
      </div>
      {/* Thumbnail Gallery */}
      {mediaContent?.length > 1 && (
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {mediaContent?.map((item, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`relative flex-shrink-0 w-16 h-20 rounded border-2 overflow-hidden transition-all ${
                index === currentIndex
                  ? 'border-accent shadow-md'
                  : 'border-border hover:border-accent/50'
              }`}
            >
              {index === 0 ? (
                <div className="w-full h-full flex items-center justify-center"
                     style={{
                       backgroundImage: 'url(/assets/images/background_image.png)',
                       backgroundSize: 'cover',
                       backgroundPosition: 'center',
                       backgroundRepeat: 'no-repeat'
                     }}>
                  <div className="w-12 h-16 flex items-center justify-center">
                    <Image
                      src={item?.url}
                      alt={item?.alt || `${title} - Miniatura ${index + 1}`}
                      className="w-full h-full object-cover rounded"
                    />
                  </div>
                </div>
              ) : (
                <Image
                  src={item?.url}
                  alt={item?.alt || `${title} - Miniatura ${index + 1}`}
                  className="w-full h-full object-cover"
                />
              )}
              {/* Media type badge */}
              <div className="absolute bottom-0 right-0 bg-black/70 text-white text-xs px-1">
                {item?.type === 'video' ? 'V' : 'I'}
              </div>
            </button>
          ))}
        </div>
      )}
      {/* Modern Image Viewer Modal */}
      {isModalOpen && currentItem?.type === 'image' && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          {/* Modal Header */}
          <div className="absolute top-0 left-0 right-0 z-60 bg-gradient-to-b from-black/80 to-transparent p-2 sm:p-4">
            <div className="flex items-center justify-between text-white">
              <div className="absolute top-4 left-4 right-4 flex justify-between items-start z-50">
                <div className="flex items-center space-x-2">
                  {mediaContent?.length > 1 && (
                    <span className="text-white text-sm bg-black/50 px-2 py-1 rounded">
                      {currentIndex + 1}/{mediaContent?.length}
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCloseModal();
                    }}
                    className="text-white hover:bg-white/20"
                  >
                    <Icon name="X" size={20} />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          {mediaContent?.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrev();
                }}
                className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-60 bg-black/50 text-white hover:bg-black/70 h-10 w-10 sm:h-12 sm:w-12"
              >
                <Icon name="ChevronLeft" size={20} className="sm:hidden" />
                <Icon name="ChevronLeft" size={24} className="hidden sm:block" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-60 bg-black/50 text-white hover:bg-black/70 h-10 w-10 sm:h-12 sm:w-12"
              >
                <Icon name="ChevronRight" size={20} className="sm:hidden" />
                <Icon name="ChevronRight" size={24} className="hidden sm:block" />
              </Button>
            </>
          )}

          {/* Image Container */}
          <div 
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseModal();
              }
            }}
          >
            <div
              ref={imageRef}
              className={`transition-transform duration-200 ${
                zoomLevel > 1 ? 'cursor-grab' : 'cursor-zoom-in'
              } ${isDragging ? 'cursor-grabbing' : ''}`}
              style={{
                transform: `scale(${zoomLevel}) translate(${imagePosition.x / zoomLevel}px, ${imagePosition.y / zoomLevel}px)`,
              }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onClick={handleImageClick}
            >
              <Image
                src={currentItem?.url}
                alt={currentItem?.alt || `${title} - Vista completa`}
                className="max-w-[90vw] max-h-[90vh] object-contain select-none"
                draggable={false}
              />
            </div>
          </div>

          {/* Bottom Controls */}
          <div className="absolute bottom-4 left-4 right-4 text-center z-50">
            <div className="text-white text-xs sm:text-sm bg-black/50 px-3 py-2 rounded inline-block">
              Toca la imagen para acercar/alejar • Arrastra para mover cuando esté ampliada
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookImageGallery;
